import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../GlobalState';
import {BaseGrid, FlexCol, FlexRow} from '../components/generic/Containers';
import {Body1, BCHeader2, BCHeader1, BCHeader3, BCHeader4, FONT_WEIGHTS} from '../components/generic/BCHeaders';
import {OnboardingAnswerButton, BCPrimaryButton, BCTertiaryButton} from '../components/generic/BCButtons';
import { BCIcon, ICONS } from '../components/generic/BCIcons';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../util/Firebase';
import { trackEvent, TRACKING_EVENTS } from '../util/TrackingHelper';
import '../css/Onboarding.css';
import {categorizeUserJourney, getJourneySteps, userJourneyPreviews} from '../util/JourneyCategorization';
import {Course} from "../models/Course";
import {Interview} from "../models/Interview";
import {Resource} from "../models/Resource";
import {ToolType} from "../models/ToolType";
import {Article} from "../models/Article";
import {Challenge} from "../models/Challenge";
import {Game} from "../models/Game";
import {Reference} from "../models/Reference";
import {LEARNING_TO_CODE_COURSES, PROGRAMMER_RESUMES_COURSES, TECH_CONCEPTS_COURSES, TECH_INTERVIEW_PREP_COURSES} from "../constants/CoursesConfig";
import {COLORS} from "../components/generic/BCColors";
import {GenericContent} from "../models/GenericContent";
import {TestimonialsComponent} from "../components/landingPage/TestimonialsComponent";
import {SingleTestimonial, TestimonialsModule} from "../components/TestimonialsModule";
import {PremiumOverlay} from "../components/PremiumOverlay";
import {TESTIMONIALS_CONFIG} from "../constants/TestimonialsConfig";
import {PremiumUpsellModal} from "../components/PremiumUpsellModal";
import { signOut } from 'firebase/auth';
import { auth } from '../util/Firebase';


const COURSE_RECOMMENDATIONS: Record<string, GenericContent[]> = {
    interested_in_code: [
        LEARNING_TO_CODE_COURSES[0], // Want to Become a Programmer? Start Here
        LEARNING_TO_CODE_COURSES[1], // Programming: Choosing and Learning Your First Language
    ],
    learning_to_code: [
        LEARNING_TO_CODE_COURSES[1], // Programming: Choosing and Learning Your First Language
        LEARNING_TO_CODE_COURSES[2], // Debugging 101
        TECH_CONCEPTS_COURSES[0], // What Are APIs
    ],
    understanding_tech_concepts: [
        TECH_CONCEPTS_COURSES[0], // What Are APIs
        TECH_CONCEPTS_COURSES[1], // A Dev's Guide to Project Management
        LEARNING_TO_CODE_COURSES[2], // Debugging 101
    ],
    job_searching: [
        PROGRAMMER_RESUMES_COURSES[1], // How to Write Your Tech Resume
        PROGRAMMER_RESUMES_COURSES[0], // Building a Tech Portfolio
        PROGRAMMER_RESUMES_COURSES[2], // How to Write a Tech Cover Letter
    ],
    interviewing: [
        TECH_INTERVIEW_PREP_COURSES[0], // Interview Prep (Technical)
        TECH_INTERVIEW_PREP_COURSES[1], // Interview Prep (Non Technical)
        PROGRAMMER_RESUMES_COURSES[0], // Building a Tech Portfolio
    ]
};

export interface ProgressStep {
    number: number;
    label: string;
    isCompleted: boolean;
    isCurrent: boolean;
}

interface ReviewsSectionProps {
    answers: Record<string, string | string[]>;
}

interface OnboardingAnswer {
    id: number;
    text: string;
    value?: string;
    icon?: any;
}

interface OnboardingQuestion {
    id: string;
    question: string;
    subtitle?: string;
    answers: OnboardingAnswer[];
    allowMultiple?: boolean;
    maxSelections?: number;
}

const ONBOARDING_QUESTIONS: OnboardingQuestion[] = [
    {
        id: 'dev_stage',
        question: 'What stage of your dev journey are you in?',
        subtitle: 'Choose the closest option',
        answers: [
            { id: 1, text: 'Interested in programming', value: 'interested', icon: ICONS.SPARKLE },
            { id: 2, text: 'learning to program', value: 'learning', icon: ICONS.CODE },
            { id: 3, text: 'Searching for a job', value: 'job_searching', icon: ICONS.REFERENCES_SEARCH },
            { id: 4, text: 'Working as a full time dev', value: 'already_working', icon: ICONS.CHALKBOARD_TEACHER }
        ],
        allowMultiple: false
    },
    {
        id: 'dev_knowledge',
        question: 'How much do you know?',
        subtitle: 'Choose the closest option',
        answers: [
            { id: 1, text: 'Just getting started', value: 'getting_started', icon: ICONS.SPARKLE },
            { id: 2, text: 'Attending a bootcamp', value: 'bootcamp', icon: ICONS.CODE },
            { id: 3, text: 'Recently graduated college', value: 'college_grad', icon: ICONS.GRADUATE },
            { id: 4, text: 'Self taught', value: 'self_taught', icon: ICONS.REFERENCES_SEARCH },
            { id: 5, text: 'Changing careers to software', value: 'career_change', icon: ICONS.CHALKBOARD_TEACHER },
            { id: 6, text: 'Working in the industry already', value: 'pro', icon: ICONS.COMPANY },
        ],
        allowMultiple: false
    },
    {
        id: 'skill_interest',
        question: 'What type of development are you interested in?',
        subtitle: 'Choose the closest option',
        answers: [
            { id: 1, text: 'Web Development', value: 'web_development', icon: ICONS.MONITOR },
            { id: 2, text: 'Mobile Development', value: 'mobile_development', icon: ICONS.PHONE_PORTRAIT },
            { id: 3, text: 'Data Science & AI', value: 'data_science_ai', icon: ICONS.SPARKLES },
            { id: 4, text: 'Backend Development', value: 'backend_development', icon: ICONS.CODE },
            { id: 5, text: 'DevOps & Security', value: 'cloud_devops', icon: ICONS.SERVER },
            { id: 6, text: "Other/Not Sure (that's okay!)", value: 'other', icon: ICONS.LESSONS }

        ],
        allowMultiple: false
    },
    {
        id: 'skill_growth',
        question: 'What skill are you most excited to improve?',
        subtitle: 'Choose as many as you want',
        answers: [
            { id: 1, text: 'Learn about development', value: 'learn_about_development', icon: ICONS.SPARKLE },
            { id: 2, text: 'Improve my resume', value: 'resume', icon: ICONS.RESUME },
            { id: 3, text: 'Stand out in interviews', value: 'interviews', icon: ICONS.PEOPLE },
            { id: 4, text: 'Strengthen my dev skills', value: 'dev_skills', icon: ICONS.CODE },

        ],
        allowMultiple: true,
    },
];

const ONBOARDING_STEPS = [
    {
        id: 'questions',
        ctaText: 'Show me my path',
        stepType: 'questions'
    },
    {
        id: 'journey',
        ctaText: 'Next',
        stepType: 'path'
    },
    {
        id: 'previews',
        ctaText: 'I’m Ready to get started',
        stepType: 'path'
    },
    {
        id: 'final',
        ctaText: 'Next',
        stepType: 'checkout'
    }
]

export const OnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, userProgressData, setUserProgressData } = useGlobalState();
    const [stepIndex, setStepIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
    const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>([]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showMobileModal, setShowMobileModal] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isStepAnimating, setIsStepAnimating] = useState(false);
    const [hasInitialLoaded, setHasInitialLoaded] = useState(false);

    const currentQuestion = ONBOARDING_QUESTIONS[questionIndex];


    useEffect(() => {
        if (currentQuestion && !isAnimating) {
            const savedAnswer = answers[currentQuestion.id];
            if (savedAnswer) {
                const savedValues = Array.isArray(savedAnswer) ? savedAnswer : [savedAnswer];
                const ids = currentQuestion.answers
                    .filter(a => savedValues.includes(a.value || ''))
                    .map(a => a.id);
                setSelectedAnswerIds(ids);
            } else {
                // Only clear selected answer IDs when not animating
                setSelectedAnswerIds([]);
            }
            // Reset transitioning state when question changes
            setIsTransitioning(false);
        }
    }, [questionIndex, stepIndex, answers, currentQuestion?.id, isAnimating]);

    useEffect(() => {
        if (!user) {
            navigate('/');
        } else {
            trackEvent(TRACKING_EVENTS.ONBOARDING_STARTED, {
                user_id: user.uid
            });
        }
    }, [user, navigate]);

    // Initial page load animation
    useEffect(() => {
        const timer = setTimeout(() => {
            setHasInitialLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const scrollToTop = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        };
        requestAnimationFrame(scrollToTop);
        const t = setTimeout(scrollToTop, 50);
        return () => clearTimeout(t);
    }, [stepIndex, questionIndex]);

    const handleAnswerSelect = (answerId: number) => {
        if (!currentQuestion || isAnimating) return;

        if (currentQuestion.allowMultiple) {
            const newSelection = selectedAnswerIds.includes(answerId)
                ? selectedAnswerIds.filter(id => id !== answerId)
                : [...selectedAnswerIds, answerId];

            if (currentQuestion.maxSelections && newSelection.length > currentQuestion.maxSelections) {
                return;
            }
            setSelectedAnswerIds(newSelection);
        } else {
            setIsTransitioning(true);
            setSelectedAnswerIds([answerId]);

            // Save the answer immediately for single-selection questions
            const selectedAnswer = currentQuestion.answers.find(a => a.id === answerId);
            const answerValue = selectedAnswer?.value || selectedAnswer?.text || '';

            setAnswers(prev => ({
                ...prev,
                [currentQuestion.id]: answerValue
            }));

            // Track the answer
            trackEvent(TRACKING_EVENTS.ONBOARDING_QUESTION_ANSWERED, {
                question_id: currentQuestion.id,
                answer: answerValue,
                step: questionIndex + 1
            });

            // Start animation sequence
            setIsAnimating(true);
            
            // Fade out, then change question, then fade in
            setTimeout(() => {
                // Scroll to top smoothly
                window.scrollTo({top: 0, behavior: 'smooth'});
                
                setSelectedAnswerIds([]);
                setQuestionIndex(prev => prev + 1);
                setIsTransitioning(false);
                
                // Fade back in
                setTimeout(() => {
                    setIsAnimating(false);
                }, 50);
            }, 300);
        }
    };

    const handleNext = async () => {
        if (isStepAnimating) return;
        
        if (ONBOARDING_STEPS[stepIndex].id === 'questions') {
            // This should only be called for the final question (multiple selection)
            if (!currentQuestion || selectedAnswerIds.length === 0) return;

            const selectedValues = selectedAnswerIds.map(id => {
                const answer = currentQuestion.answers.find(a => a.id === id);
                return answer?.value || answer?.text || '';
            });

            const answerValue = selectedValues; // Always array for multiple selection
            setAnswers(prev => ({
                ...prev,
                [currentQuestion.id]: answerValue
            }));

            // Track the answer
            trackEvent(TRACKING_EVENTS.ONBOARDING_QUESTION_ANSWERED, {
                question_id: currentQuestion.id,
                answer: answerValue,
                step: questionIndex + 1
            });

            // Animate step transition
            setIsStepAnimating(true);
            setTimeout(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
                setStepIndex(prev => prev + 1);
                setTimeout(() => {
                    setIsStepAnimating(false);
                }, 50);
            }, 300);
        } else if (ONBOARDING_STEPS[stepIndex].id !== 'final') {
            // Animate step transition
            setIsStepAnimating(true);
            setTimeout(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
                setStepIndex(prev => prev + 1);
                setTimeout(() => {
                    setIsStepAnimating(false);
                }, 50);
            }, 300);
        }
    };


    const handleBack = async () => {
        if (isStepAnimating) return;
        
        if (ONBOARDING_STEPS[stepIndex].id === 'questions' && questionIndex > 0) {
            // Add animation for back navigation too
            setIsAnimating(true);
            setTimeout(() => {
                // Scroll to top smoothly
                window.scrollTo({top: 0, behavior: 'smooth'});
                
                setQuestionIndex(prev => prev - 1);
                setTimeout(() => {
                    setIsAnimating(false);
                }, 50);
            }, 300);
        } else {
            // Animate step transition for back navigation
            setIsStepAnimating(true);
            setTimeout(() => {
                window.scrollTo({top: 0, behavior: 'smooth'});
                setStepIndex(prev => prev - 1);
                setTimeout(() => {
                    setIsStepAnimating(false);
                }, 50);
            }, 300);
        }
    }

    const handleOnboardingSuccess = async () => {
        if (!user) return;

        try {
            const userDocRef = doc(firestore, 'users', user.uid);
            const journeyCategory = categorizeUserJourney(answers);
            const updateData = {
                hasOnboarded: true,
                onboardingAnswers: answers,
                journeyCategory: journeyCategory,
                onboardingCompletedAt: new Date().toISOString()
            };
            await updateDoc(userDocRef, updateData);

            // Update local user progress data to prevent redirect loop
            if (userProgressData) {
                setUserProgressData({
                    ...userProgressData,
                    ...updateData
                });
            }

            trackEvent(TRACKING_EVENTS.ONBOARDING_COMPLETED, {
                user_id: user.uid,
                answers: answers,
                journey_category: journeyCategory
            });

            // Navigate to home after successful completion
            navigate('/');
        } catch (error) {
            console.error('Error completing onboarding:', error);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const canProceed = selectedAnswerIds.length > 0;
    const currentStep = ONBOARDING_STEPS[stepIndex]
    
    return (
        <FlexCol align={"center"} justify={"center"} className="onboarding-page-wrapper" style={{ minHeight: '100%', width: '100%', background: COLORS.ONBOARDING_BACKGROUND, overflowX: 'hidden'}}>
            <FlexCol 
                flex={1} 
                heightPercent={100} 
                maxWidth={60} 
                className={`onboarding-page ${hasInitialLoaded ? 'onboarding-page-loaded' : 'onboarding-page-loading'}`} 
                gap={2} 
                widthPercent={94} 
                pTop={2} 
                pBottom={2} 
                style={{
                    background: COLORS.ONBOARDING_BACKGROUND, 
                    minHeight: '100vh',
                    opacity: hasInitialLoaded ? 1 : 0,
                    transform: hasInitialLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.6s ease-out'
                }}
            >
                <StepProgressBar 
                    currentStep={ONBOARDING_STEPS[stepIndex].stepType}
                    onMobileMoreClick={() => setShowMobileModal(true)}
                    showMobileMore={true}
                    canGoBack={questionIndex > 0 || stepIndex > 0}
                    hasInitialLoaded={hasInitialLoaded}
                />
                <FlexCol 
                    widthPercent={100} 
                    style={{
                        opacity: isStepAnimating ? 0 : (hasInitialLoaded ? 1 : 0),
                        transform: isStepAnimating ? 'translateY(10px)' : (hasInitialLoaded ? 'translateY(0)' : 'translateY(15px)'),
                        transition: isStepAnimating ? 'all 0.3s ease-in-out' : 'all 0.8s ease-out 0.4s'
                    }}
                >
                    {currentStep.id === 'questions' && <QuestionsStage key={`question-${questionIndex}-${currentQuestion?.id}`} currentQuestion={currentQuestion} handleAnswerSelect={handleAnswerSelect} selectedAnswerIds={selectedAnswerIds} questionIndex={questionIndex} isAnimating={isAnimating}/>}
                    {currentStep.id === 'journey' && <ReviewsSection answers={answers}/>}
                    {currentStep.id === 'previews' && <PreviewSections answers={answers}/>}
                    {currentStep.id === 'final' && <PremiumUpsellModal source="onboarding_completion" onSuccess={handleOnboardingSuccess} />}
                </FlexCol>
                {currentStep.id !== 'final' && <FlexRow 
                    widthPercent={100} 
                    align="center" 
                    justify="center" 
                    className={'onboarding-question-container'}
                    style={{
                        opacity: hasInitialLoaded ? 1 : 0,
                        transform: hasInitialLoaded ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'all 0.9s ease-out 0.6s'
                    }}
                >
                    {(() => {
                        if (currentStep.id === 'questions') {
                            const isLastQuestion = questionIndex === ONBOARDING_QUESTIONS.length - 1;
                            if (isLastQuestion && canProceed && !isTransitioning) {
                                return <BCPrimaryButton className={'onboarding-questions-cta'} text={currentStep.ctaText} onClick={handleNext} disabled={!canProceed} style={{padding: '1em 4em'}}/>;
                            }
                            return null;
                        }
                        return canProceed ? <BCPrimaryButton text={currentStep.ctaText} onClick={handleNext} disabled={!canProceed || isStepAnimating} style={{padding: '1em 4em'}}/> : null;
                    })()}
                </FlexRow> }
            </FlexCol>

            {/* More Options Modal */}
            {showMobileModal && (
                <MoreOptionsModal 
                    onBack={handleBack}
                    onLogout={handleLogout}
                    onClose={() => setShowMobileModal(false)}
                    canGoBack={questionIndex > 0 || stepIndex > 0}
                />
            )}
        </FlexCol>
    );
};

export const StepProgressBar: React.FC<{currentStep: string, onMobileMoreClick?: () => void, showMobileMore?: boolean, canGoBack?: boolean, hasInitialLoaded?: boolean}> = ({ currentStep, onMobileMoreClick, showMobileMore = false, canGoBack = false, hasInitialLoaded = true }) => {
    const steps: ProgressStep[] = [
        {
            number: 1,
            label: 'Questions',
            isCompleted: currentStep === 'path' || currentStep === 'checkout',
            isCurrent: currentStep === 'questions'
        },
        {
            number: 2,
            label: 'Your Path',
            isCompleted: currentStep === 'checkout',
            isCurrent: currentStep === 'path'
        },
        {
            number: 3,
            label: 'Get Started',
            isCompleted: false,
            isCurrent: currentStep === 'checkout'
        }
    ];

    // Simple progress using percentages
    const currentStepIndex = steps.findIndex(s => s.isCurrent);
    const progressWidth = currentStepIndex === 0 ? '0%' : currentStepIndex === 1 ? '50%' : '100%';

    return (
        <FlexRow 
            widthPercent={100} 
            justify="center" 
            align="center" 
            style={{
                position: 'relative',
                opacity: hasInitialLoaded ? 1 : 0,
                transform: hasInitialLoaded ? 'translateY(0)' : 'translateY(-10px)',
                transition: 'all 0.7s ease-out 0.2s'
            }}
        >
            {/* Desktop Progress Bar */}
            <FlexRow justify="space-between" align="center" flex={1} maxWidth={40} style={{position: 'relative'}} className="desktop-progress-bar">
                <div
                    style={{
                        position: 'absolute',
                        top: '.8em',
                        left: '2em',
                        right: '2em',
                        height: '2px',
                        background: '#E5E7EB',
                        zIndex: 0
                    }}
                />

                <div
                    style={{
                        position: 'absolute',
                        top: '.8em',
                        left: '2em',
                        right: '2em',
                        height: '2px',
                        background: 'var(--color-primary)',
                        zIndex: 1,
                        transition: 'all 0.4s ease',
                        transform: `scaleX(${currentStepIndex === 0 ? 0 : currentStepIndex === 1 ? 0.5 : 1})`,
                        transformOrigin: 'left'
                    }}
                />

                {steps.map((step, index) => (
                    <FlexCol key={step.number} align="center" gap={0.75} style={{zIndex: 2, position: 'relative'}}>
                        <div
                            style={{
                                width: '25px',
                                height: '25px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: step.isCompleted || step.isCurrent ? 'var(--color-primary)' : 'white',
                                border: step.isCompleted || step.isCurrent ? '2px solid #3277c1' : '2px solid #E5E7EB',
                                color: step.isCompleted || step.isCurrent ? 'white' : '#9CA3AF',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                transition: 'all 0.3s ease',
                                cursor: 'default',
                                boxShadow: step.isCompleted || step.isCurrent ? '0 2px 8px rgba(50, 119, 193, 0.3)' : 'none'
                            }}
                        >
                            {step.isCompleted ? (<BCIcon icon={ICONS.CHECKMARK} style={{fontSize: '1rem', color: 'white'}}/>) : (step.number)}
                        </div>
                        <Body1 text={step.label} fontSize={.7} textAlign={'center'} whiteSpace={"nowrap"} fontWeight={step.isCurrent ? FONT_WEIGHTS.semiBold : FONT_WEIGHTS.light} color={step.isCurrent ? "PRIMARY_DEFAULT" : "TEXT_SECONDARY"}/>
                    </FlexCol>
                ))}
            </FlexRow>

            {/* Mobile Progress Bar */}
            <FlexRow className="mobile-progress-bar" widthPercent={70} style={{display: 'none'}}>
                <FlexCol flex={1} gap={0.5}>
                    <FlexRow justify="space-between" align="center">
                        <Body1 text={`Step ${currentStepIndex + 1} of ${steps.length}`} fontSize={0.9} color="TEXT_SECONDARY" />
                        <Body1 text={steps[currentStepIndex]?.label} fontSize={0.9} fontWeight={FONT_WEIGHTS.light} />
                    </FlexRow>
                    <div style={{
                        width: '100%',
                        height: '6px',
                        background: '#E5E7EB',
                        borderRadius: '3px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            height: '100%',
                            background: 'var(--color-primary)',
                            borderRadius: '3px',
                            transition: 'width 0.4s ease',
                            width: `${((currentStepIndex + 1) / steps.length) * 100}%`
                        }} />
                    </div>
                </FlexCol>
            </FlexRow>

            {/* More Button - shown on both web and mobile */}
            {showMobileMore && onMobileMoreClick && (
                <FlexRow 
                    className="more-button" 
                    onClick={onMobileMoreClick}
                    align="center"
                    justify="center"
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '50%',
                        transition: 'background-color 0.2s ease',
                        flexShrink: 0
                    }}
                >
                    <BCIcon icon={ICONS.MORE} style={{color: COLORS.TEXT_SECONDARY, fontSize: '1.5rem'}} />
                </FlexRow>
            )}
        </FlexRow>
    );
};

const QuestionsStage: React.FC<{currentQuestion: any, selectedAnswerIds: number[], handleAnswerSelect: any, questionIndex: number, isAnimating: boolean}> = ({currentQuestion, selectedAnswerIds, handleAnswerSelect, questionIndex, isAnimating}) => {
    return (
        <FlexCol 
            align="center"
            alignSelf={"center"}
            gap={2} 
            widthPercent={96}
            style={{
                background: COLORS.ONBOARDING_BACKGROUND,
                opacity: isAnimating ? 0 : 1,
                transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
                transition: 'all 0.3s ease-in-out'
            }}
        >
            <FlexCol align="center">
                <Body1 text={`${questionIndex + 1} of ${ONBOARDING_QUESTIONS.length}`} textAlign="center" color={"TEXT_SECONDARY"} fontSize={.9}/>
                <BCHeader2 text={currentQuestion.question} textAlign="center" fontSize={2.5} fontWeight={FONT_WEIGHTS.light} className="onboarding-question-heading"/>
                {currentQuestion.subtitle && (
                    <Body1 text={currentQuestion.subtitle} mTop={.5} textAlign="center" color={"TEXT_SECONDARY"} fontWeight={FONT_WEIGHTS.light} fontSize={.9}/>
                )}
            </FlexCol>
            <BaseGrid widthPercent={100} gap={1} columns={2} mobileColumns={1}>
                {currentQuestion.answers.map((answer: any) => {
                    const isSelected = selectedAnswerIds.includes(answer.id);
                    return (
                        <OnboardingAnswerButton key={`${currentQuestion.id}-${answer.id}`} text={answer.text} iconLeft={answer.icon} isSelected={isSelected} onClick={() => handleAnswerSelect(answer.id)}/>
                    );
                })}
            </BaseGrid>
        </FlexCol>
    )
}

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({answers = {}}) => {
    const userCategory = categorizeUserJourney(answers);
    const journeySteps = getJourneySteps(userCategory);
    const [animateJourney, setAnimateJourney] = useState(false);

    // Trigger animations when component mounts
    useEffect(() => {
        // Add a small delay to ensure the initial hidden state is rendered first
        const timer = setTimeout(() => {
            setAnimateJourney(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <FlexCol widthPercent={100} align="center" justify="center" gap={1} mBottom={1}>
            <FlexCol align="center" gap={0.2}>
                <Body1 text="Based on your answers," textAlign="center" fontSize={.9} fontWeight={FONT_WEIGHTS.light} color={"TEXT_SECONDARY"}/>
                <BCHeader1 text="Your Journey is" textAlign="center" fontSize={2} />
            </FlexCol>

            <FlexCol align="center" gap={0} widthPercent={100} maxWidth={40}>
                {journeySteps.map((step, index) => (
                    <FlexCol 
                        widthPercent={100} 
                        key={step.id} 
                        align="center" 
                        gap={0}
                    >
                        <FlexRow 
                            widthPercent={100} 
                            align="center" 
                            gap={1} 
                            className={`journey-step ${animateJourney ? 'journey-step-animated' : ''}`}
                            style={{
                                position: 'relative',
                                animationDelay: animateJourney ? `${index * 0.4}s` : '0s'
                            }}
                        >
                            <FlexRow align="center" widthPercent={100} gap={1} padding={.5} style={{
                                background: step.isCompleted ? 'var(--color-primary-50)' : 'white',
                                borderRadius: '28px',
                                border: !step.isCompleted && !step.isCurrent ? '1px solid var(--color-primary-300)' : '1px solid var(--color-primary-100)',
                                flex: 1,
                                minHeight: '56px',
                                paddingLeft: '12px',
                                paddingRight: step.isCompleted ? '12px' : '20px',
                                position: 'relative',
                                boxShadow: step.isCurrent ? '0 2px 12px rgba(50, 118, 192, 0.8)' : 'none',
                            }}>
                                {step.isCurrent && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-10px',
                                        left: '20px',
                                        padding: '2px 12px',
                                        background: 'var(--color-primary)',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        borderRadius: '12px',
                                        whiteSpace: 'nowrap',
                                        zIndex: 2
                                    }}>
                                        Start Here!
                                    </div>
                                )}
                                <div style={{
                                    width: '26px',
                                    height: '26px',
                                    borderRadius: '50%',
                                    background: step.isCompleted ? 'var(--color-primary)' : 'white',
                                    border: step.isCompleted ? 'none' : step.isCurrent ? '2px solid #B8D4F5' : '1px solid #E0E0E0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    {step.isCompleted ? (
                                        <BCIcon icon={ICONS.CHECKMARK} style={{ color: 'white', fontSize: '1.8rem' }}/>
                                    ) : (
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: 'transparent'
                                        }} />
                                    )}
                                </div>

                                <Body1 text={step.label} fontWeight={FONT_WEIGHTS.normal} fontSize={1}
                                    style={{color: step.isCompleted ? 'var(--color-primary)' : 'var(--text-color)', flex: 1}}/>

                                {step.isCompleted && (
                                    <FlexRow align="center" gap={0.3} style={{
                                        background: 'var(--color-primary-100)',
                                        padding: '6px 12px',
                                        borderRadius: '16px',
                                        flexShrink: 0
                                    }}>
                                        <Body1 text="You've Tested Out!" fontWeight={FONT_WEIGHTS.light} fontSize={.6} color={"TEXT_SECONDARY"}/>
                                        <BCIcon icon={ICONS.MORE_INFO} style={{fontSize: `1rem`}}/>
                                    </FlexRow>
                                )}
                            </FlexRow>
                        </FlexRow>

                        {index < journeySteps.length - 1 && (
                            <div 
                                className={`journey-connector ${animateJourney ? 'journey-connector-animated' : ''}`}
                                style={{
                                    width: '1px', 
                                    height: '24px', 
                                    background: 'var(--color-primary)', 
                                    margin: '0 auto',
                                    animationDelay: animateJourney ? `${(index + 1) * 0.4}s` : '0s'
                                }} 
                            />
                        )}
                    </FlexCol>
                ))}
            </FlexCol>
        </FlexCol>
    );
};

export const PreviewSections: React.FC<ReviewsSectionProps> = ({answers}) => {
    const userCategory = categorizeUserJourney(answers);
    const recommendedContent = userJourneyPreviews(answers);
    const [animateItems, setAnimateItems] = useState(false);
    const [settleItems, setSettleItems] = useState(false);

    // Trigger animation when component mounts
    useEffect(() => {
        setAnimateItems(true);
        // Trigger settle animations after list slide-up completes (2s + small delay)
        const settleTimer = setTimeout(() => {
            setSettleItems(true);
        }, 1000);
        
        return () => {
            clearTimeout(settleTimer);
        };
    }, []);

    return (
        <FlexCol gap={0} maxWidth={45} alignSelf={"center"}>
            <FlexCol align="center" gap={0.2} mBottom={2}>
                <Body1 text="Based on your answers," textAlign="center" fontSize={.9} fontWeight={FONT_WEIGHTS.light} color={"TEXT_SECONDARY"}/>
                <BCHeader1 text="We think you'll love these" textAlign="center" fontSize={2} />
            </FlexCol>
            <div style={{ position: 'relative' }}>
                <FlexCol maxHeight={30} style={{overflow: 'hidden'}} gap={0}>
                    <FlexCol 
                        heightPercent={100} 
                        gap={0.75} 
                        pHorizontal={1}
                        className={animateItems ? 'preview-list-animated' : 'preview-list-container'}
                    >
                        {recommendedContent.map((content: any, index: number) => (
                            <OnboardingPreviewContent
                                key={`content-${index}`}
                                content={content}
                                index={index}
                                shouldSettle={settleItems}
                            />
                        ))}
                    </FlexCol>
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '100px',
                        background: 'linear-gradient(to bottom, transparent, rgba(249, 250, 251, 0.2), #F9FAFB)',
                        pointerEvents: 'none'
                    }}/>
                </FlexCol>
            </div>
            <div style={{height: '2px', background: COLORS.BORDER_DEFAULT, width: '100%'}}/>
        </FlexCol>
    )
}



const OnboardingPreviewContent: React.FC<{content: GenericContent, index: number, shouldSettle: boolean}> = ({content, index, shouldSettle}) => {
    // Import the necessary types at the top of the file
    let label = '';
    let backgroundColor = '';

    // Determine content type and set label/color accordingly
    if (content instanceof Course) {
        label = 'Course';
        backgroundColor = 'rgba(251, 146, 60, 0.9)'; // Orange
    } else if (content instanceof Interview) {
        label = 'Interview';
        backgroundColor = 'rgba(59, 130, 246, 0.9)'; // Blue
    } else if (content instanceof Resource) {
        label = 'Resource';
        backgroundColor = 'rgba(34, 197, 94, 0.9)'; // Green
    } else if (content instanceof ToolType) {
        label = 'AI Tool';
        backgroundColor = 'rgba(147, 51, 234, 0.9)'; // Purple
    } else if (content instanceof Article) {
        label = 'Article';
        backgroundColor = 'rgba(107, 114, 128, 0.9)'; // Gray
    } else if (content instanceof Challenge) {
        label = 'Challenge';
        backgroundColor = 'rgba(239, 68, 68, 0.9)'; // Red
    } else if (content instanceof Game) {
        label = 'Game';
        backgroundColor = 'rgba(245, 158, 11, 0.9)'; // Amber
    } else if (content instanceof Reference) {
        label = 'Reference';
        backgroundColor = 'rgba(16, 185, 129, 0.9)'; // Emerald
    }

    if (!label) return null;

    return (
        <FlexRow key={`course-${index}`}
                 className={'preview-item-container ' + (shouldSettle ? 'preview-item-settle' : 'preview-item')}
                 style={{
                     background: 'white',
                     border: '1px solid #E5E7EB',
                     borderRadius: '8px',
                     animationDelay: shouldSettle ? `${index * 0.1}s` : '0s',
                     position: 'relative',
                     maxHeight: '80px'
                 }}

        >
            {content.thumbnail && (
                <div style={{
                    flexShrink: 0,
                    width: 'auto',
                    maxWidth: '30%',
                    overflow: 'hidden',
                    alignSelf: 'stretch'
                }}>
                    <img
                        src={content.thumbnail}
                        alt={content.name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            maxHeight: '80px'
                        }}
                    />
                </div>
            )}
            {content.thumbnail && (
                <FlexRow className={'preview-item-type-label'} align={"center"} gap={.5} borderRadius={.25} justify={"center"} style={{
                    position: 'absolute',
                    width: '10em',
                    height: '2em',
                    bottom: 0,
                    left: 0,
                    background: backgroundColor.replace('0.9)', '1)'),
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 300,
                    alignItems: 'center',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                    <BCIcon 
                        icon={
                            content instanceof Course ? ICONS.CHALKBOARD_TEACHER :
                            content instanceof Interview ? ICONS.VIDEO :
                            content instanceof Resource ? ICONS.PDF :
                            content instanceof ToolType ? ICONS.SPARKLE :
                            content instanceof Article ? ICONS.PEN :
                            content instanceof Challenge ? ICONS.CODE :
                            content instanceof Game ? ICONS.SPARKLE :
                            content instanceof Reference ? ICONS.BOOK :
                            ICONS.SPARKLE
                        }
                        style={{
                            fontSize: '1rem'
                        }}
                    />
                    {label}
                </FlexRow>
            )}
            <FlexCol heightPercent={100} pHorizontal={1} pVertical={.5} gap={0.5} flex={1} justify="space-between">
                <Body1 className={'onboarding-preview-item-title'} text={content.name} fontWeight={FONT_WEIGHTS.light} fontSize={.9} color={'TEXT_SECONDARY'}/>
                <FlexRow className={'onboarding-preview-item-description-container'} gap={2} align="center">
                    {content instanceof Course && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.LESSONS} 
                            text={`${content.numberOfLessons} Lessons`} 
                        />
                    )}

                    {(content instanceof Course || content instanceof Interview) && content.time && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.CLOCK} 
                            text={content.time} 
                        />
                    )}

                    {content instanceof Resource && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.PDF} 
                            text="Download PDF" 
                        />
                    )}

                    {content instanceof Article && content.date && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.CALENDAR} 
                            text={content.date} 
                        />
                    )}

                    {content instanceof Challenge && content.challengeItems && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.CODE} 
                            text={`${content.challengeItems.length} Challenges`} 
                        />
                    )}

                    {content instanceof Reference && content.referenceItems && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.BOOK} 
                            text={`${content.referenceItems.length} Items`} 
                        />
                    )}

                    {content instanceof ToolType && (
                        <OnboardingPreviewInfo 
                            icon={ICONS.SPARKLE} 
                            text="AI-Powered Tool" 
                        />
                    )}
                </FlexRow>
            </FlexCol>
        </FlexRow>
    )
}

const OnboardingPreviewInfo: React.FC<{icon: any, text: string}> = ({icon, text}) => {
    return (
        <FlexRow align="center" gap={0.5}>
            <BCIcon icon={icon} style={{color: '#9CA3AF', fontSize: '0.7rem'}}/>
            <Body1 text={text} fontSize={.7} color={'TEXT_SECONDARY'}/>
        </FlexRow>
    )
}

const MoreOptionsModal: React.FC<{
    onBack: () => void;
    onLogout: () => void;
    onClose: () => void;
    canGoBack: boolean;
}> = ({ onBack, onLogout, onClose, canGoBack }) => {
    const handleBackClick = () => {
        onClose();
        onBack();
    };

    const handleLogoutClick = () => {
        onClose();
        onLogout();
    };

    return (
        <>
            {/* Backdrop */}
            <div 
                className="mobile-modal-backdrop"
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1000,
                }}
            />
            
            {/* Modal */}
            <FlexCol
                className="more-options-modal"
                style={{
                    position: 'fixed',
                    top: '20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    zIndex: 1001,
                    minWidth: '280px',
                    overflow: 'hidden'
                }}
                gap={0}
            >
                {canGoBack && (
                    <FlexRow
                        onClick={handleBackClick}
                        align="center"
                        gap={1}
                        padding={1.25}
                        style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s ease'
                        }}
                        className="modal-option"
                    >
                        <BCIcon icon={ICONS.LEFT_ARROW} style={{ color: COLORS.TEXT_SECONDARY, fontSize: '1.2rem' }} />
                        <Body1 text="Go Back" fontSize={1} />
                    </FlexRow>
                )}
                
                <FlexRow
                    onClick={handleLogoutClick}
                    align="center"
                    gap={1}
                    padding={1.25}
                    style={{
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                    }}
                    className="mobile-modal-option"
                >
                    <BCIcon icon={ICONS.ACCOUNT} style={{ color: COLORS.TEXT_SECONDARY, fontSize: '1.2rem' }} />
                    <Body1 text="Log Out" fontSize={1} />
                </FlexRow>
            </FlexCol>
        </>
    );
};